/**
 * @file pages/well/MegTestPage.js
 * @description Well Butler — Meg (insulation resistance) test calculator.
 *
 * Three test points down the line, three readings per point. Enter as many or as
 * few as you took — every reading is graded on its own, and the pass/fail pattern
 * ACROSS points isolates which section is bad:
 *
 *   1. At the pressure switch  → buried wire + drop cable + motor
 *   2. At the wellhead         → drop cable + motor
 *   3. At the pump (pulled)    → motor only
 *
 * A pass at any point clears everything it covers; a fail above a pass pins the
 * fault to the segment between them. Insulation thresholds follow the Franklin
 * Electric AIM manual (500V megger); wire-to-wire is graded for opens/shorts and
 * always tells the tech to compare against the motor's spec-sheet ohms.
 *
 * All client-side math — works with zero signal in the field.
 */
import React, { useState } from 'react';
import { Button, Input, Card } from '../../design-system';
import { pushHistory } from '../../lib/wellHistory';
import { authFetch, API } from '../../lib/api';
import { WellPage, Disclaimer, VERIFY_LINE, Term } from './wellShared';

// ─── Grading ────────────────────────────────────────────────────────────────

const LEVEL_RANK = { pass: 0, watch: 1, fail: 2 };
const worst = (a, b) => (a == null ? b : b == null ? a : LEVEL_RANK[b] > LEVEL_RANK[a] ? b : a);

/** Grade one insulation reading (megohms to ground). dry = motor out of the well. */
function gradeInsulation(mohm, ol, dry) {
  if (ol) return { level: 'pass', text: 'OL — beyond the meter’s range. Best reading possible.' };
  if (mohm == null || Number.isNaN(mohm)) return null;
  if (dry) {
    if (mohm >= 20) return { level: 'pass', text: '≥ 20 MΩ dry — like-new insulation.' };
    if (mohm >= 10) return { level: 'pass', text: '≥ 10 MΩ dry — normal for a used motor out of the well.' };
    if (mohm >= 1) return { level: 'watch', text: '1–10 MΩ is low for a DRY motor — inspect the leads and splice closely.' };
    return { level: 'fail', text: '< 1 MΩ on a dry motor — insulation is compromised.' };
  }
  if (mohm >= 2) return { level: 'pass', text: '≥ 2 MΩ in the well — excellent, new-condition insulation.' };
  if (mohm >= 0.5) return { level: 'pass', text: '0.5–2 MΩ — serviceable for a motor in water. Worth re-checking next visit.' };
  if (mohm >= 0.02) return { level: 'watch', text: '20 kΩ–500 kΩ — insulation damage is developing. Plan the repair.' };
  if (mohm >= 0.01) return { level: 'fail', text: '10–20 kΩ — insulation is damaged. Schedule replacement.' };
  return { level: 'fail', text: '< 10 kΩ — insulation has failed.' };
}

/** Grade the wire-to-wire (winding circuit) reading, in plain ohms. */
function gradeContinuity(ohms, ol) {
  if (ol) return { level: 'fail', text: 'OL / open — a broken conductor, bad splice, or open winding in this stretch.' };
  if (ohms == null || Number.isNaN(ohms)) return null;
  if (ohms < 0.2) return { level: 'watch', text: 'Near zero — possible shorted winding (or meter leads touching). Re-check.' };
  if (ohms <= 30) return { level: 'pass', text: 'Continuity good. Compare the exact ohms to the motor’s spec sheet for its HP.' };
  if (ohms <= 200) return { level: 'watch', text: 'Higher than a healthy pump circuit usually reads — suspect a failing splice or connection.' };
  return { level: 'fail', text: 'Way above any normal winding + cable resistance — treat as a failing circuit.' };
}

/**
 * Isolate which segment is bad from the per-point pass/fail pattern.
 * statuses: { ps, wh, pu } each 'pass' | 'watch' | 'fail' | null.
 */
function isolateSegments(s) {
  const seg = { buried: 'unknown', drop: 'unknown', motor: 'unknown' };
  if (s.pu === 'pass') seg.motor = 'good';
  if (s.wh === 'pass') { seg.drop = 'good'; if (seg.motor === 'unknown') seg.motor = 'good'; }
  if (s.ps === 'pass') {
    seg.buried = 'good';
    if (seg.drop === 'unknown') seg.drop = 'good';
    if (seg.motor === 'unknown') seg.motor = 'good';
  }
  if (s.pu === 'fail') seg.motor = 'bad';
  if (s.wh === 'fail') {
    if (seg.motor === 'good') seg.drop = 'bad';
    else if (seg.motor === 'bad') { if (seg.drop === 'unknown') seg.drop = 'suspect'; }
    else { if (seg.drop !== 'good') seg.drop = 'suspect'; if (seg.motor !== 'good') seg.motor = 'suspect'; }
  }
  if (s.ps === 'fail') {
    if (seg.drop === 'good' && seg.motor === 'good') seg.buried = 'bad';
    else if (seg.drop === 'bad' || seg.motor === 'bad') { if (seg.buried === 'unknown') seg.buried = 'suspect'; }
    else {
      seg.buried = 'suspect';
      if (seg.drop === 'unknown') seg.drop = 'suspect';
      if (seg.motor === 'unknown') seg.motor = 'suspect';
    }
  }
  return seg;
}

// ─── Page ───────────────────────────────────────────────────────────────────

const POINTS = [
  {
    key: 'ps', title: '1. At the pressure switch / tank tee', dry: false,
    covers: 'Measures the buried wire to the well + the drop cable down the hole + the pump, all together.',
  },
  {
    key: 'wh', title: '2. At the wellhead', dry: false,
    covers: 'Disconnect at the well cap and test the well side — measures just the drop cable + the pump.',
  },
  {
    key: 'pu', title: '3. At the pump (pulled, on the ground)', dry: true,
    covers: 'Pump out of the well — measures the motor and its leads only.',
  },
];

const READS = [
  {
    key: 'ww', label: 'Wire 1 → Wire 2', unit: 'Ω', kind: 'continuity',
    note: 'Use the meter’s ohms / continuity mode for this one — not the insulation-test button. Healthy is a small number (a few ohms). Flip the unit if your meter shows kΩ or MΩ.',
  },
  { key: 'g1', label: 'Wire 1 → Ground', unit: 'MΩ', kind: 'insulation' },
  { key: 'g2', label: 'Wire 2 → Ground', unit: 'MΩ', kind: 'insulation' },
];

const CHIP = {
  pass: { bg: '#e6f4ea', fg: '#0a7d33', label: 'PASS' },
  watch: { bg: '#fdf3e1', fg: '#a86a00', label: 'WATCH' },
  fail: { bg: '#fdecea', fg: '#b3261e', label: 'FAIL' },
  good: { bg: '#e6f4ea', fg: '#0a7d33', label: 'GOOD' },
  bad: { bg: '#fdecea', fg: '#b3261e', label: 'BAD' },
  suspect: { bg: '#fdf3e1', fg: '#a86a00', label: 'SUSPECT' },
  unknown: { bg: '#eef1f4', fg: '#5b6570', label: 'NOT PROVEN' },
};

function Chip({ level }) {
  const c = CHIP[level] || CHIP.unknown;
  return (
    <span style={{
      background: c.bg, color: c.fg, borderRadius: 999, padding: '2px 10px',
      fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.03em', whiteSpace: 'nowrap',
    }}>{c.label}</span>
  );
}

const SEGMENTS = [
  { key: 'buried', label: 'Buried wire (house → wellhead)' },
  { key: 'drop', label: 'Drop cable (down the well)' },
  { key: 'motor', label: 'Pump motor' },
];

const emptyPoint = () => ({
  ww: '', wwOl: false, wwUnit: 'ohm',
  g1: '', g1Ol: false, g2: '', g2Ol: false,
  // Step-voltage re-reads at 1000V (IEEE 43): offered when a ground reading grades WATCH.
  g1Step: '', g1StepOl: false, g2Step: '', g2StepOl: false,
});

const WW_UNIT_FACTOR = { ohm: 1, kohm: 1e3, Mohm: 1e6 };

/**
 * Step-voltage comparison (IEEE 43): healthy insulation reads about the same at
 * 1000V as at 500V; a drop >25% under the higher stress means moisture or
 * deteriorating insulation — escalate. Holding steady means marginal-but-stable.
 */
function applyStepTest(baseGrade, baseMohm, stepMohm, stepOl) {
  if (!baseGrade || baseGrade.level !== 'watch') return baseGrade;
  if (stepOl) {
    return { level: 'watch', text: baseGrade.text + ' Step test at 1000V read OL — held up under stress; marginal but stable. Re-test next visit.' };
  }
  if (stepMohm == null || Number.isNaN(stepMohm) || baseMohm == null || baseMohm <= 0) return baseGrade;
  const dropPct = Math.round(((baseMohm - stepMohm) / baseMohm) * 100);
  if (stepMohm < baseMohm * 0.75) {
    return { level: 'fail', text: `Dropped ${dropPct}% under 1000V stress (${baseMohm} → ${stepMohm} MΩ) — moisture or deteriorating insulation confirmed by step-voltage test (IEEE 43).` };
  }
  return { level: 'watch', text: `Held steady under 1000V stress (${baseMohm} → ${stepMohm} MΩ) — marginal but stable. Re-test at the next seasonal visit.` };
}

/** Per-point completeness (James's rule): any point with ANY reading needs all 3. */
function incompletePoints(form) {
  const out = [];
  for (const p of POINTS) {
    const f = form[p.key];
    const has = (k) => (f[k] !== '' && f[k] != null) || !!f[k + 'Ol'];
    const entered = ['ww', 'g1', 'g2'].filter(has);
    if (entered.length > 0 && entered.length < 3) {
      const missing = ['ww', 'g1', 'g2'].filter((k) => !has(k))
        .map((k) => (k === 'ww' ? 'Wire 1 → Wire 2' : k === 'g1' ? 'Wire 1 → Ground' : 'Wire 2 → Ground'));
      out.push(`${p.title.replace(/^\d+\.\s*/, '')} — missing ${missing.join(' and ')}`);
    }
  }
  return out;
}

export default function MegTestPage() {
  const [form, setForm] = useState({ ps: emptyPoint(), wh: emptyPoint(), pu: emptyPoint() });
  const [result, setResult] = useState(null);
  const [custEmail, setCustEmail] = useState('');
  const [custAddr, setCustAddr] = useState('');
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState(null);
  const [controlBoxAck, setControlBoxAck] = useState(false);
  const [testVoltage, setTestVoltage] = useState('500');

  const setVal = (pt, k) => (e) => setForm((f) => ({ ...f, [pt]: { ...f[pt], [k]: e.target.value } }));
  const toggleOl = (pt, k) => () => setForm((f) => ({ ...f, [pt]: { ...f[pt], [k + 'Ol']: !f[pt][k + 'Ol'] } }));

  function calculate() {
    const num = (v) => (v === '' || v == null ? null : Number(v));
    const perPoint = {};
    let anyReading = false;

    for (const p of POINTS) {
      const f = form[p.key];
      const grades = {
        ww: gradeContinuity(num(f.ww) == null ? null : num(f.ww) * WW_UNIT_FACTOR[f.wwUnit || 'ohm'], f.wwOl),
        g1: applyStepTest(gradeInsulation(num(f.g1), f.g1Ol, p.dry), num(f.g1), num(f.g1Step), f.g1StepOl),
        g2: applyStepTest(gradeInsulation(num(f.g2), f.g2Ol, p.dry), num(f.g2), num(f.g2Step), f.g2StepOl),
      };
      const insul = worst(grades.g1 && grades.g1.level, grades.g2 && grades.g2.level);
      const cont = grades.ww && grades.ww.level;
      if (insul || cont) anyReading = true;
      perPoint[p.key] = { grades, insul: insul || null, cont: cont || null };
    }
    if (!anyReading) { setResult({ empty: true }); return; }

    // 'watch' neither clears nor condemns a segment for isolation purposes.
    const forIso = (lvl) => (lvl === 'watch' ? null : lvl);
    const insulSeg = isolateSegments({ ps: forIso(perPoint.ps.insul), wh: forIso(perPoint.wh.insul), pu: forIso(perPoint.pu.insul) });
    const contSeg = isolateSegments({ ps: forIso(perPoint.ps.cont), wh: forIso(perPoint.wh.cont), pu: forIso(perPoint.pu.cont) });

    const segRank = { unknown: 0, good: 1, suspect: 2, bad: 3 };
    const segs = {};
    for (const s of SEGMENTS) {
      const a = insulSeg[s.key]; const b = contSeg[s.key];
      segs[s.key] = segRank[b] > segRank[a] ? b : a;
    }

    // Next-best-test coaching
    let nextTest = null;
    const failedSomewhere = ['ps', 'wh', 'pu'].some((k) => perPoint[k].insul === 'fail' || perPoint[k].cont === 'fail');
    if (failedSomewhere) {
      if ((perPoint.ps.insul === 'fail' || perPoint.ps.cont === 'fail') && !perPoint.wh.insul && !perPoint.wh.cont) {
        nextTest = 'Repeat the same readings at the wellhead. If they pass there, the problem is in the buried wire between the house and the well.';
      } else if ((perPoint.wh.insul === 'fail' || perPoint.wh.cont === 'fail') && !perPoint.pu.insul && !perPoint.pu.cont) {
        nextTest = 'If you pull the pump, repeat the readings right at the motor leads. Pass there means the drop cable (or a splice) is the problem, not the pump.';
      }
    }

    const badSegs = SEGMENTS.filter((s) => segs[s.key] === 'bad').map((s) => s.label);
    const suspectSegs = SEGMENTS.filter((s) => segs[s.key] === 'suspect').map((s) => s.label);
    let headline, headlineLevel;
    if (badSegs.length) { headline = `Fault isolated: ${badSegs.join(' + ')}`; headlineLevel = 'fail'; }
    else if (failedSomewhere) { headline = 'Failing readings found — not fully isolated yet'; headlineLevel = 'fail'; }
    else if (suspectSegs.length || ['ps', 'wh', 'pu'].some((k) => perPoint[k].insul === 'watch' || perPoint[k].cont === 'watch')) {
      headline = 'No hard failure, but something’s marginal'; headlineLevel = 'watch';
    } else { headline = 'Everything you tested reads healthy'; headlineLevel = 'pass'; }

    const res = { perPoint, segs, nextTest, headline, headlineLevel };
    setResult(res);
    pushHistory({
      kind: 'megtest',
      title: `Meg test — ${CHIP[headlineLevel].label}`,
      summary: headline + (badSegs.length ? '' : suspectSegs.length ? ` (${suspectSegs.join(', ')})` : ''),
    });
  }

  return (
    <WellPage
      title="Meg test calculator"
      intro={
        <>Kill the power first. Take your <Term word="megger">An insulation tester — it puts high DC voltage (usually 500V) on the wire and measures how many megohms leak to ground. More megohms = healthier insulation.</Term> readings
        at whichever points you can reach, punch them in, and get a grade per reading — plus which section of the system the pattern points at.
        On a 3-wire motor, test on the well side of the control box.</>
      }
    >
      {!controlBoxAck && (
        <Card variant="elevated" padding="md" style={{ border: '3px solid #b3261e', background: 'var(--surface-danger-soft, #fdecea)' }}>
          <div style={{ fontSize: '2.6rem', textAlign: 'center', lineHeight: 1 }} aria-hidden="true">⚠️</div>
          <div style={{ fontSize: '1.35rem', fontWeight: 800, textAlign: 'center', color: '#b3261e', margin: '8px 0 10px', letterSpacing: '.02em' }}>
            STOP — GO LOOK BEFORE YOU TEST
          </div>
          <p style={{ fontWeight: 'var(--weight-bold)', fontSize: '1.02rem', margin: '8px 0' }}>
            Is there a PUMP CONTROL BOX on this system? Walk over and physically check RIGHT NOW —
            it's usually a gray box on the wall near the pressure tank.
          </p>
          <p style={{ margin: '8px 0' }}>
            3-wire pumps have one. Megger voltage <strong>DESTROYS control boxes</strong> — the
            capacitors, relays, and protectors inside do not survive a 500–1000V test.
          </p>
          <p style={{ margin: '8px 0' }}>
            <strong>If there IS a control box:</strong> disconnect the wires on the WELL side of it and
            test only toward the well. Never test into the box or through it.
          </p>
          <p style={{ margin: '8px 0 14px' }}>
            <strong>No control box (2-wire pump):</strong> you're clear.
          </p>
          <Button onClick={() => setControlBoxAck(true)} fullWidth size="lg">
            I physically checked — the control box is out of the circuit
          </Button>
        </Card>
      )}

      {controlBoxAck && (<>
      <Card variant="elevated" padding="md" style={{ marginBottom: 'var(--space-3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <div style={{ fontWeight: 'var(--weight-bold)', fontSize: 'var(--text-body-sm)' }}>Meter test voltage</div>
          <select value={testVoltage} onChange={(e) => setTestVoltage(e.target.value)}
            style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-default, #d0d4d9)', background: 'var(--surface-primary, #fff)' }}>
            <option value="250">250V</option>
            <option value="500">500V (standard)</option>
            <option value="1000">1000V</option>
          </select>
        </div>
        <div style={{ fontSize: 'var(--text-caption)', color: 'var(--text-tertiary)', marginTop: 6, lineHeight: 1.4 }}>
          Pass/fail bands assume 500V. 250V flatters weak insulation — treat marginal results with suspicion.
          When a reading grades WATCH, a step-test box appears: repeat that reading at 1000V and enter it —
          a big drop under the higher stress confirms moisture or deterioration.
        </div>
      </Card>

      {POINTS.map((p) => (
        <Card key={p.key} variant="elevated" padding="md" style={{ marginBottom: 'var(--space-3)' }}>
          <div style={{ fontWeight: 'var(--weight-bold)', marginBottom: 2 }}>{p.title}</div>
          <div style={{ fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-3)' }}>{p.covers}</div>
          {READS.map((r) => {
            const f = form[p.key];
            const ol = f[r.key + 'Ol'];
            const grade = result && !result.empty ? result.perPoint[p.key].grades[r.key] : null;
            return (
              <div key={r.key} style={{ marginBottom: 'var(--space-2)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  <div style={{ width: 130, fontSize: 'var(--text-body-sm)', fontWeight: 'var(--weight-semibold)' }}>{r.label}</div>
                  <div style={{ flex: 1 }}>
                    <Input
                      type="number" inputMode="decimal" value={ol ? '' : f[r.key]}
                      onChange={setVal(p.key, r.key)}
                      rightAddon={r.kind === 'continuity' ? undefined : r.unit}
                      disabled={ol} placeholder={ol ? 'OL' : ''}
                    />
                  </div>
                  {r.kind === 'continuity' && (
                    <select
                      value={f.wwUnit}
                      onChange={setVal(p.key, 'wwUnit')}
                      aria-label="Wire-to-wire unit"
                      style={{
                        padding: '8px 6px', borderRadius: 8, border: '1px solid var(--border-default, #d0d4d9)',
                        background: 'var(--surface-primary, #fff)', fontSize: 'var(--text-body-sm)',
                      }}
                    >
                      <option value="ohm">Ω</option>
                      <option value="kohm">kΩ</option>
                      <option value="Mohm">MΩ</option>
                    </select>
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                    <input type="checkbox" checked={ol} onChange={toggleOl(p.key, r.key)} /> OL
                  </label>
                </div>
                {r.note && !grade && (
                  <div style={{ fontSize: 'var(--text-caption)', color: 'var(--text-tertiary)', lineHeight: 1.4, marginTop: 2, marginLeft: 130 + 8 }}>
                    {r.note}
                  </div>
                )}
                {grade && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, marginLeft: 130 + 8 }}>
                    <Chip level={grade.level} />
                    <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', lineHeight: 1.4 }}>{grade.text}</span>
                  </div>
                )}
                {r.kind === 'insulation' && grade && (grade.level === 'watch' || f[r.key + 'Step'] !== '' || f[r.key + 'StepOl']) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginTop: 6, marginLeft: 130 + 8 }}>
                    <div style={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--weight-semibold)', whiteSpace: 'nowrap' }}>
                      Step test @ 1000V:
                    </div>
                    <div style={{ flex: 1 }}>
                      <Input
                        type="number" inputMode="decimal"
                        value={f[r.key + 'StepOl'] ? '' : f[r.key + 'Step']}
                        onChange={setVal(p.key, r.key + 'Step')} rightAddon="MΩ"
                        disabled={f[r.key + 'StepOl']} placeholder={f[r.key + 'StepOl'] ? 'OL' : 'same reading, 1000V'}
                      />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      <input type="checkbox" checked={f[r.key + 'StepOl']} onChange={toggleOl(p.key, r.key + 'Step')} /> OL
                    </label>
                  </div>
                )}
              </div>
            );
          })}
        </Card>
      ))}

      <Button onClick={calculate} fullWidth size="lg">Calculate</Button>

      {result && result.empty && (
        <p style={{ color: 'var(--text-secondary)', marginTop: 'var(--space-3)' }}>
          Enter at least one reading first — even a single wire-to-ground number gets graded.
        </p>
      )}

      {result && !result.empty && (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <Card variant="elevated" padding="md" style={{ marginBottom: 'var(--space-3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
              <Chip level={result.headlineLevel} />
              <div style={{ fontWeight: 'var(--weight-bold)' }}>{result.headline}</div>
            </div>
            {SEGMENTS.map((s) => (
              <div key={s.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderTop: '1px solid var(--border-subtle, #eee)' }}>
                <span style={{ fontSize: 'var(--text-body-sm)' }}>{s.label}</span>
                <Chip level={result.segs[s.key]} />
              </div>
            ))}
            {result.nextTest && (
              <p style={{ margin: 'var(--space-3) 0 0', fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                <strong>Next best test:</strong> {result.nextTest}
              </p>
            )}
          </Card>
          <Disclaimer text={VERIFY_LINE} />
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
            Insulation grades follow the Franklin Electric AIM manual thresholds for a 500V insulation tester.
            "NOT PROVEN" just means no reading you entered covers that section cleanly — not that it’s bad.
          </p>

          <Card variant="elevated" padding="md" style={{ marginTop: 'var(--space-4)' }}>
            <div style={{ fontWeight: 'var(--weight-bold)', marginBottom: 2 }}>Email this report to the customer</div>
            <div style={{ fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-3)' }}>
              Sends a plain-English + technical report with a recommendation. A copy goes to the office
              automatically. Rule: every test point you started must have all 3 readings.
            </div>
            {(() => {
              const inc = incompletePoints(form);
              if (inc.length) {
                return (
                  <p style={{ color: 'var(--color-danger, #b3261e)', fontSize: 'var(--text-body-sm)', margin: '0 0 var(--space-2)' }}>
                    Finish these before sending — {inc.join('; ')}.
                  </p>
                );
              }
              return null;
            })()}
            <div style={{ display: 'grid', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
              <Input type="text" value={custAddr} onChange={(e) => setCustAddr(e.target.value)}
                placeholder="Property address (e.g. 123 Rhine Branch, Weaverville, NC 28787)" />
              <Input type="email" inputMode="email" value={custEmail} onChange={(e) => setCustEmail(e.target.value)}
                placeholder="Customer email address" />
            </div>
            <Button
              onClick={async () => {
                setSending(true); setSendMsg(null);
                try {
                  const res = await authFetch(`${API}/v1/well/megtest-report`, {
                    method: 'POST',
                    body: JSON.stringify({ customerEmail: custEmail.trim(), propertyAddress: custAddr.trim(), points: form, testVoltage }),
                  });
                  let json = null;
                  try { json = await res.json(); } catch (_) { /* no body */ }
                  if (json && json.success) {
                    setSendMsg({ ok: true, text: `Report emailed to ${custEmail.trim()} (copy sent to the office).` });
                  } else {
                    setSendMsg({ ok: false, text: (json && json.error && json.error.message) || `Send failed (${res.status}). Try again.` });
                  }
                } catch (err) {
                  setSendMsg({ ok: false, text: 'Could not reach the server — check signal and try again. Your readings are safe on this page.' });
                }
                setSending(false);
              }}
              fullWidth
              disabled={sending || !custEmail.trim() || custAddr.trim().length < 5 || incompletePoints(form).length > 0}
            >
              {sending ? 'Sending…' : 'Email report'}
            </Button>
            {sendMsg && (
              <p style={{
                marginTop: 'var(--space-2)', fontSize: 'var(--text-body-sm)',
                color: sendMsg.ok ? 'var(--color-success, #0a7d33)' : 'var(--color-danger, #b3261e)',
              }}>{sendMsg.text}</p>
            )}
          </Card>
        </div>
      )}
      </>)}
    </WellPage>
  );
}
